/* =========================================================
   Momentos de producto — DermX Tattoo

   Tres ramas completamente separadas en gsap.matchMedia():

   - Escritorio (>=750px): un único pin de la sección y una sola timeline con
     scrub repartida en N tramos iguales, uno por momento. Los momentos están
     superpuestos (clase --stacked). Dentro de cada tramo pasan dos cosas: el
     crossfade de la fotografía (la saliente 1->0 y la entrante 0->1 a la vez)
     y el revelado del texto en tres sub-tiempos que ocupan tercios del tramo:

       tramo del momento i  ->  [ i , i+1 ) de la timeline
         i + 0/3 .. +0.233  A. entra el titular "01 — Fin del tatuaje"
         i + 1/3 .. +0.233  B. el titular encoge y sube; entra el producto
         i + 2/3 .. +0.233  C. entra el beneficio
         i + 0.9 .. i+1     respiro antes del siguiente crossfade

     El crossfade vive en el arranque del tramo (los primeros 0.2), solapado
     con el sub-tiempo A: la foto nueva ya está entrando cuando aparece su
     titular.

   - Móvil (<=749px): sin pin y sin sub-tiempos. Cada momento va en flujo
     normal con los tres textos visibles y entra con un reveal simple
     (fade + slide-up) al 80% del viewport, disparo único, sin scrub.

   - prefers-reduced-motion: no se construye nada. El CSS por defecto ya
     muestra la columna apilada con todo visible.
   ========================================================= */

(function () {
  'use strict';

  /* En el Theme Editor la sección re-inyecta su <script> y este archivo se
     vuelve a ejecutar. Si ya hay un módulo vivo, reutilizamos el suyo: así el
     registro de instancias y los listeners del editor no se duplican. */
  if (window.DermxProductMoments) {
    window.DermxProductMoments.initAll();
    return;
  }

  var GSAP_VERSION = '3.13.0';
  var GSAP_URL = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/gsap.min.js';
  var SCROLLTRIGGER_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/gsap/' + GSAP_VERSION + '/ScrollTrigger.min.js';

  var DESKTOP = '(min-width: 750px)';
  var MOBILE = '(max-width: 749px)';
  var MOTION_OK = '(prefers-reduced-motion: no-preference)';
  var MOTION_REDUCED = '(prefers-reduced-motion: reduce)';

  /* --- Reparto de cada tramo de 1 unidad (= 1 momento) --- */

  var THIRD = 1 / 3;
  // Cada entrada de texto consume el 70% de su tercio y deja el resto de
  // margen: el texto termina de asentarse antes de que empiece el siguiente.
  var REVEAL = THIRD * 0.7;
  // El crossfade cabe dentro del primer tercio, delante del sub-tiempo A.
  var CROSSFADE = 0.2;
  // Cola muerta al final del tramo, para que el último momento tenga el mismo
  // respiro que los demás antes de despinear.
  var TAIL = 1 - (2 * THIRD + REVEAL);

  var TEXT_OFFSET = 40; // slide-up de cada entrada, en px
  var TITLE_SCALE = 0.78; // cuánto encoge el titular en el sub-tiempo B
  var TITLE_LIFT = -12; // cuánto sube el titular en el sub-tiempo B, en px

  var MOBILE_OFFSET = 48; // slide-up del reveal de bloque en móvil

  /* Una instancia por section.id: { mm } */
  var instances = new Map();

  /* ---------------------------------------------------------
     Carga de GSAP + ScrollTrigger (una sola vez por página)

     assets/smooth-scroll.js ya define window.dermxGsapReady en el <head> con
     el mismo formato de resolución. Si está, esta sección se cuelga de esa
     promesa y hereda el scroller del ScrollSmoother; si no está (o la
     plantilla no lo carga), la construimos aquí con lo mínimo.
     --------------------------------------------------------- */

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-gsap-src="' + src + '"]');

      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', function () {
            resolve();
          });
          existing.addEventListener('error', reject);
        }
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = false; // preserva el orden gsap -> ScrollTrigger
      script.dataset.gsapSrc = src;
      script.addEventListener('load', function () {
        script.dataset.loaded = 'true';
        resolve();
      });
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  }

  function isPluginRegistered(name) {
    return !!(window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  function globalPlugin(name) {
    return window[name] || (window.gsap && window.gsap.core && window.gsap.core.globals()[name]);
  }

  function ensureGsap() {
    if (window.dermxGsapReady) return window.dermxGsapReady;

    window.dermxGsapReady = Promise.resolve()
      .then(function () {
        if (!window.gsap) return loadScript(GSAP_URL);
        return null;
      })
      .then(function () {
        if (!window.gsap) throw new Error('[product-moments] GSAP no disponible');
        if (!globalPlugin('ScrollTrigger')) return loadScript(SCROLLTRIGGER_URL);
        return null;
      })
      .then(function () {
        var gsap = window.gsap;
        var ScrollTrigger = globalPlugin('ScrollTrigger');

        if (!ScrollTrigger) throw new Error('[product-moments] ScrollTrigger no disponible');
        if (!isPluginRegistered('ScrollTrigger')) gsap.registerPlugin(ScrollTrigger);

        // En móvil, mostrar/ocultar la barra de direcciones cambia innerHeight:
        // sin esto ScrollTrigger recalcularía a media animación y daría saltos.
        ScrollTrigger.config({ ignoreMobileResize: true });

        return { gsap: gsap, ScrollTrigger: ScrollTrigger };
      });

    return window.dermxGsapReady;
  }

  /* ---------------------------------------------------------
     Utilidades
     --------------------------------------------------------- */

  var refreshTimer = null;

  function refreshSoon(ScrollTrigger, delay) {
    if (!ScrollTrigger) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      ScrollTrigger.refresh();
    }, delay || 60);
  }

  /* Una imagen que aún no ha cargado puede cambiar la altura del layout, y con
     ella los offsets del pin: refrescamos cuando llegue. */
  function refreshOnImages(root, ScrollTrigger) {
    root.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener(
        'load',
        function () {
          refreshSoon(ScrollTrigger);
        },
        { once: true }
      );
    });
  }

  /* Precarga: los momentos a partir del segundo salen en loading="lazy" y
     preload="none" desde el Liquid. Antes de que le toque el turno a uno lo
     pasamos a eager con prioridad alta y pedimos decode() / load(), para que
     el crossfade no empiece sobre una foto a medio pintar ni sobre un vídeo
     sin un solo fotograma si el usuario baja rápido. Se llama con un momento
     de adelanto, así que siempre hay margen. */
  function warmMoment(momentEl) {
    if (!momentEl || momentEl.dataset.pmWarm === 'true') return;
    momentEl.dataset.pmWarm = 'true';

    momentEl.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.loading = 'eager';
      if ('fetchPriority' in img) img.fetchPriority = 'high';
      if (typeof img.decode === 'function') {
        img.decode().catch(function () {
          /* Una imagen que falla al decodificar no debe romper la animación. */
        });
      }
    });

    momentEl.querySelectorAll('video').forEach(function (video) {
      // load() reinicia el elemento: por eso solo se toca el vídeo que aún no
      // ha empezado a cargar, y siempre antes de que le llegue el turno.
      if (video.preload !== 'none') return;
      video.preload = 'auto';
      video.load();
    });
  }

  /* El vídeo no lleva autoplay en el markup: solo suena —bueno, solo se
     mueve, van mudos— el del momento visible. Así N vídeos superpuestos no
     compiten por ancho de banda ni por CPU, y con movimiento reducido no se
     reproduce ninguno porque esa rama no construye nada.

     Ningún navegador reproduce un <video> que no está renderizado, y el
     rechazo de play() es silencioso: por eso las capas de medios se animan con
     `opacity` y no con `autoAlpha` (que a opacidad 0 pone visibility: hidden),
     y por eso se reintenta unas cuantas veces en vez de darlo por perdido al
     primer intento. */
  var PLAY_ATTEMPTS = 4;

  function playVideo(video) {
    if (!video) return;
    // play() pone paused a false de inmediato: sirve de guarda contra
    // llamadas solapadas mientras la promesa está en vuelo.
    if (!video.paused) return;

    /* El primer intento puede caer en el fotograma exacto en el que el momento
       se activa, con su capa todavía a opacidad 0. De ahí los reintentos.
       Agotados, se deja en paz: hay navegadores que simplemente no van a
       reproducir (modo de bajo consumo en iOS, ahorro de datos) y no tiene
       sentido insistir en cada frame de scroll. Se queda el póster: la imagen
       del block o el primer fotograma del propio vídeo. */
    var failed = parseInt(video.dataset.pmFailed || '0', 10);
    if (failed >= PLAY_ATTEMPTS) return;

    var played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(function () {
        video.dataset.pmFailed = String(failed + 1);
      });
    }
  }

  function pauseVideo(video) {
    // Sin rebobinar: al volver a ese momento el bucle sigue donde estaba.
    if (!video || video.paused) return;
    video.pause();
  }

  /* Quién está en pantalla lo decide un IntersectionObserver, no un
     ScrollTrigger.

     En escritorio la sección está pineada, y un ScrollTrigger cuyo trigger es
     el propio elemento pineado acaba midiendo sobre un elemento que
     ScrollTrigger está moviendo: el estado "en pantalla" se encendía y se
     apagaba a mitad del pin y pausaba el vídeo recién arrancado. El observer
     mira la intersección real con el viewport —que es exactamente la pregunta
     que queremos hacer— y le da igual el pin, los transforms y el orden en que
     ScrollTrigger refresca sus medidas.

     Además entrega una primera llamada con el estado actual nada más observar,
     así que no hace falta arrancar a mano lo que ya está en pantalla. */
  function observeVisibility(elements, onChange) {
    if (typeof IntersectionObserver !== 'function') return null;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          onChange(entry.target, entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    elements.forEach(function (element) {
      observer.observe(element);
    });

    return observer;
  }

  // Cada vez que un momento vuelve a estar en pantalla, su vídeo tiene otra
  // oportunidad: lo que falló con la capa a medio entrar puede funcionar ahora.
  function resetVideoAttempts(video) {
    if (video) video.dataset.pmFailed = '0';
  }

  /* ---------------------------------------------------------
     Ciclo de vida por sección
     --------------------------------------------------------- */

  function destroy(sectionId) {
    var instance = instances.get(sectionId);
    if (!instance) return;
    // revert() mata tweens, sets, pins y ScrollTriggers de las dos ramas.
    instance.mm.revert();
    instances.delete(sectionId);
  }

  function init(root) {
    if (!root) return;

    var sectionId = root.dataset.sectionId;
    if (!sectionId) return;

    destroy(sectionId); // evita duplicados al re-renderizar en el editor

    ensureGsap()
      .then(function (lib) {
        // El editor puede haber quitado la sección mientras cargaba el CDN.
        if (!root.isConnected) return;
        build(root, sectionId, lib.gsap, lib.ScrollTrigger);
      })
      .catch(function (error) {
        // Sin GSAP el CSS por defecto ya muestra la columna con todo visible.
        root.classList.add('product-moments--static');
        console.warn(error);
      });
  }

  function build(root, sectionId, gsap, ScrollTrigger) {
    var select = gsap.utils.selector(root); // todo scopeado a esta sección
    var moments = select('[data-pm-moment]');

    if (!moments.length) return;

    function partOf(momentEl, name) {
      return momentEl.querySelector('[data-pm-' + name + ']');
    }

    var medias = moments.map(function (el) {
      return partOf(el, 'media');
    });
    var contents = moments.map(function (el) {
      return partOf(el, 'content');
    });
    var steps = moments.map(function (el) {
      return partOf(el, 'step');
    });
    var products = moments.map(function (el) {
      return partOf(el, 'product');
    });
    var benefits = moments.map(function (el) {
      return partOf(el, 'benefit');
    });

    /* video_tag no admite parámetros con guiones, así que el vídeo no lleva
       data-attribute propio: se localiza por etiqueta dentro del momento.
       Hueco (null) en los momentos que solo tienen imagen. */
    var count = moments.length;

    var videos = moments.map(function (el) {
      return el.querySelector('video');
    });

    function pauseAllVideos() {
      videos.forEach(pauseVideo);
    }

    var fills = select('[data-pm-fill]');
    var dots = select('[data-pm-dot]');

    /* Un block puede dejar en blanco cualquiera de los tres textos: el Liquid
       no imprime el elemento y aquí queda un hueco en el array. Todo lo que
       toca esos arrays lo filtra o lo comprueba antes. */
    function present(list) {
      return list.filter(Boolean);
    }

    function tweenTo(timeline, target, vars, position) {
      if (!target) return;
      timeline.to(target, vars, position);
    }

    var mm = gsap.matchMedia();
    instances.set(sectionId, { mm: mm });

    /* --- Indicador de progreso ---

       Barra segmentada, un segmento por momento. No es una animación aparte:
       lo alimenta el propio progreso del ScrollTrigger del pin, así que
       siempre dice exactamente lo mismo que la timeline. Los estilos se
       escriben a pelo (no con gsap.set) porque cambian en cada frame y el
       contexto de matchMedia no los revierte: los limpia el cleanup. */
    function paintProgress(progress) {
      var scaled = progress * count;

      fills.forEach(function (fill, index) {
        // 1 en los momentos ya vistos, la fracción en curso en el activo,
        // 0 en los que faltan.
        var local = Math.min(1, Math.max(0, scaled - index));
        fill.style.transform = 'scaleX(' + local + ')';
      });
    }

    function clearProgress() {
      fills.forEach(function (fill) {
        fill.style.transform = '';
      });
      dots.forEach(function (dot) {
        dot.classList.remove('is-active');
      });
    }

    /* --- Movimiento reducido: nada construido, estado final del CSS --- */
    mm.add(MOTION_REDUCED, function () {
      root.classList.add('product-moments--static');
      return function () {
        root.classList.remove('product-moments--static');
      };
    });

    /* --- Escritorio: un pin, una timeline, N tramos iguales --- */
    mm.add(DESKTOP + ' and ' + MOTION_OK, function () {
      root.classList.add('product-moments--stacked');

      /* Estado de partida: solo la primera foto visible, todos los textos
         fuera. Los textos van con autoAlpha, que además de la opacidad reserva
         su hueco y los saca del árbol de accesibilidad hasta que entran, así
         que la columna no da saltos al irse rellenando.

         Las capas de medios, en cambio, van con `opacity` pelada: autoAlpha
         les pondría visibility: hidden a opacidad 0 y ningún navegador
         reproduce un vídeo que no está renderizado. */
      gsap.set(present(medias), { opacity: 0 });
      gsap.set(medias[0], { opacity: 1 });
      gsap.set(present(contents), { autoAlpha: 1, y: 0 });
      gsap.set(present(steps).concat(present(products), present(benefits)), {
        autoAlpha: 0,
        y: TEXT_OFFSET,
      });

      warmMoment(moments[0]);
      warmMoment(moments[1]);

      var active = -1;
      var inView = false;

      /* Reproducción: como mucho un vídeo a la vez, el del momento activo, y
         solo mientras la sección se ve. Sin la condición de visibilidad el
         primer vídeo empezaría a sonar —a moverse— nada más cargar la página,
         con la sección todavía tres pantallas más abajo. */
      function syncPlayback() {
        videos.forEach(function (video, index) {
          if (inView && index === active) {
            playVideo(video);
          } else {
            pauseVideo(video);
          }
        });
      }

      /* El momento activo es el único que recibe clics y el que se dibuja por
         encima: al entrar en su tramo sube de z-index y su foto hace el
         crossfade por delante de la saliente. Hacia atrás pasa lo mismo con el
         anterior, que es el que entra en ese sentido. */
      function setActive(index) {
        if (index === active) return;
        if (active > -1) moments[active].classList.remove('is-active');
        active = index;
        moments[index].classList.add('is-active');
        resetVideoAttempts(videos[index]);
        syncPlayback();

        // El indicador es decorativo (la <ol> va con aria-hidden), así que
        // esto es solo un gancho de estilo: quién es el segmento en curso.
        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });

        warmMoment(moments[index + 1]); // un momento de adelanto
      }

      function sync(self) {
        setActive(Math.min(count - 1, Math.floor(self.progress * count)));
        paintProgress(self.progress);
        /* setActive corta en seco si el índice no ha cambiado, así que sin
           esto un vídeo que el navegador haya pausado por su cuenta —o cuyo
           primer play() se comiera un rechazo— se quedaría clavado en su
           primer fotograma para siempre. Es barato: play/pause salen antes de
           hacer nada si el vídeo ya está como toca. */
        syncPlayback();
      }

      var timeline = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          id: 'product-moments-' + sectionId + '-desktop',
          trigger: root,
          start: 'top top',
          /* Distancia total = momentos x vh configurados. Función +
             invalidateOnRefresh para que se recalcule en cada refresh
             (resize, cambio de número de blocks en el editor, etc.). */
          end: function () {
            var perMoment = parseFloat(root.dataset.momentScroll) || 100;
            return '+=' + Math.round(window.innerHeight * (perMoment / 100) * count);
          },
          scrub: true, // 1:1 con el scroll, sin suavizado añadido
          pin: root,
          pinSpacing: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: sync,
          onRefresh: sync,
        },
      });

      moments.forEach(function (momentEl, index) {
        // Cada momento arranca su tramo en la posición `index` de la timeline:
        // duración total = count, así que cada tramo mide exactamente 1/N.
        var at = index;

        /* Crossfade: las dos opacidades se mueven a la vez y en la misma
           ventana. La saliente lleva power2.in para aguantar cerca de 1 al
           principio: con las dos en lineal el punto medio dejaría ver el fondo
           de la sección entre las dos fotos. */
        if (index > 0) {
          tweenTo(timeline, medias[index], { opacity: 1, duration: CROSSFADE, ease: 'none' }, at);
          tweenTo(
            timeline,
            medias[index - 1],
            { opacity: 0, duration: CROSSFADE, ease: 'power2.in' },
            at
          );
          // El texto del momento saliente se va con su foto.
          tweenTo(
            timeline,
            contents[index - 1],
            { autoAlpha: 0, y: TITLE_LIFT, duration: CROSSFADE * 0.7, ease: 'power1.in' },
            at
          );
        }

        // A. Primer tercio: número + nombre del momento.
        tweenTo(timeline, steps[index], { autoAlpha: 1, y: 0, duration: REVEAL }, at);

        // B. Segundo tercio: el titular encoge y sube para dejar sitio, y
        //    debajo entra el producto/atributo.
        tweenTo(
          timeline,
          steps[index],
          { scale: TITLE_SCALE, y: TITLE_LIFT, duration: REVEAL },
          at + THIRD
        );
        tweenTo(timeline, products[index], { autoAlpha: 1, y: 0, duration: REVEAL }, at + THIRD);

        // C. Tercer tercio: el beneficio, debajo del producto.
        tweenTo(
          timeline,
          benefits[index],
          { autoAlpha: 1, y: 0, duration: REVEAL },
          at + 2 * THIRD
        );
      });

      /* Tramo muerto final: deja la duración total en exactamente `count` (una
         unidad por momento) para que el último tenga el mismo respiro que los
         demás antes de que la sección se despinee. */
      timeline.to({}, { duration: TAIL }, count - TAIL);

      /* Ventana de reproducción: mientras la sección se vea, esté clavada o no.
         Cubre todo el recorrido del pin y también el tramo previo, en el que la
         sección ya asoma pero todavía no se ha fijado. */
      var visibility = observeVisibility([root], function (_element, isVisible) {
        inView = isVisible;
        syncPlayback();
      });

      // Sin IntersectionObserver preferimos que se reproduzca de más que de
      // menos: es un vídeo cada vez, y el pin ya limita cuándo se ve.
      if (!visibility) inView = true;

      setActive(0);
      paintProgress(0);

      return function () {
        if (visibility) visibility.disconnect();
        if (timeline.scrollTrigger) timeline.scrollTrigger.kill(true);
        timeline.kill();
        clearProgress();
        pauseAllVideos();
        if (active > -1) moments[active].classList.remove('is-active');
        root.classList.remove('product-moments--stacked');
      };
    });

    /* --- Móvil: sin pin, un reveal de disparo único por bloque --- */
    mm.add(MOBILE + ' and ' + MOTION_OK, function () {
      /* Aquí los tres textos se leen de una vez dentro del bloque: no hay
         sub-tiempos que preparar, solo el estado de entrada del bloque
         entero. `opacity` y no autoAlpha por lo mismo que en escritorio: el
         bloque envuelve al vídeo y visibility: hidden impediría reproducirlo.
         El disparador de reproducción (top bottom) se adelanta al del reveal
         (top 80%), así que el vídeo arranca con el bloque aún a opacidad 0. */
      gsap.set(moments, { opacity: 0, y: MOBILE_OFFSET });

      warmMoment(moments[0]);

      var tweens = moments.map(function (momentEl, index) {
        return gsap.to(momentEl, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          scrollTrigger: {
            id: 'product-moments-' + sectionId + '-mobile-' + index,
            trigger: momentEl,
            start: 'top 80%',
            once: true, // disparo único, sin scrub
            invalidateOnRefresh: true,
            onEnter: function () {
              warmMoment(moments[index + 1]); // un momento de adelanto
            },
          },
        });
      });

      /* Reproducción en móvil: sin pin no hay "momento activo", así que cada
         vídeo se ata a su propio paso por el viewport. Mismo observer que en
         escritorio, aquí con un elemento por momento. Va por libre del reveal
         (que es de una sola vez) porque tiene que seguir encendiendo y apagando
         en los dos sentidos. */
      var visibility = observeVisibility(moments, function (momentEl, isVisible) {
        var index = moments.indexOf(momentEl);
        if (index < 0 || !videos[index]) return;

        if (isVisible) {
          warmMoment(momentEl);
          resetVideoAttempts(videos[index]);
          playVideo(videos[index]);
        } else {
          pauseVideo(videos[index]);
        }
      });

      return function () {
        tweens.forEach(function (tween) {
          if (tween.scrollTrigger) tween.scrollTrigger.kill(true);
          tween.kill();
        });
        if (visibility) visibility.disconnect();
        pauseAllVideos();
      };
    });

    refreshOnImages(root, ScrollTrigger);
    refreshSoon(ScrollTrigger);
  }

  /* ---------------------------------------------------------
     Arranque + eventos del Theme Editor
     --------------------------------------------------------- */

  function initAll() {
    document.querySelectorAll('.product-moments[data-section-id]').forEach(init);
  }

  window.DermxProductMoments = { init: init, initAll: initAll, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', function () {
    refreshSoon(globalPlugin('ScrollTrigger'));
  });

  /* Resize con debounce: cambia la altura del viewport y con ella la distancia
     total del pin, que se recalcula porque `end` es una función con
     invalidateOnRefresh. */
  window.addEventListener('resize', function () {
    refreshSoon(globalPlugin('ScrollTrigger'), 150);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.product-moments[data-section-id]');
    if (!root) return;
    // Reconstruye desde cero con el número de momentos que haya ahora.
    init(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('.product-moments[data-section-id]');
    if (!root) return;
    destroy(root.dataset.sectionId);
  });

  // Al seleccionar/deseleccionar en el editor cambian offsets y alturas.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach(function (
    eventName
  ) {
    document.addEventListener(eventName, function () {
      refreshSoon(globalPlugin('ScrollTrigger'));
    });
  });
})();
